// src/api/authInterceptor.ts

import type { AxiosInstance, AxiosError } from 'axios'
import axios from 'axios'
import { Capacitor } from '@capacitor/core'

import { tokenService } from './tokenService'
import { refreshCoordinator } from './refreshCoordinator'
import { useAuthFacade } from '@/auth/facade/authFacade'
import { appAuth } from '@/auth/platform/appAuth'
import { logger } from '@myorg/type-lib'

export function setupAuthInterceptor(
  api: AxiosInstance,
  baseURL: string
) {
  /**
   * =========================================
   * 🚀 REQUEST INTERCEPTOR
   * =========================================
   */
  api.interceptors.request.use(async config => {
    const isApp = Capacitor.isNativePlatform()

    if (isApp) {
      const token = await appAuth.getAccessToken()

      if (token) {
        config.headers = config.headers ?? {}
        config.headers.Authorization = `Bearer ${token}`
      }

      config.withCredentials = false
    } else {
      config.withCredentials = true
    }

    return config
  })

  /**
   * =========================================
   * 📥 RESPONSE INTERCEPTOR
   * =========================================
   */
  api.interceptors.response.use(
    res => res,
    async (error: AxiosError) => {
      const original = error.config as any
      if (!original) throw error

      /**
       * ❌ skip refresh endpoint
       */
      if (
        original._skipGlobalError ||
        original.url?.includes('/api/client/refresh')
      ) {
        throw error
      }

      /**
       * ❌ 非 401 不處理
       */
      if (error.response?.status !== 401) {
        throw error
      }

      /**
       * ❌ retry only once
       */
      if (original._retry) {
        throw error
      }

      original._retry = true

      const isApp = Capacitor.isNativePlatform()

      /**
       * =========================================
       * 🌐 WEB MODE → BFF session dead
       * =========================================
       */
      if (!isApp) {
        logger.warn('[Interceptor] Web 401 → force logout')

        const auth = useAuthFacade()
        void refreshCoordinator.runLogout(async () => {
          await auth.logout({ isForce: true })
        })

        throw error
      }

      /**
       * =========================================
       * 📱 APP MODE → JWT refresh flow
       * =========================================
       */

      /**
       * 1. 已有人在 refresh → 等待
       */
      if (!refreshCoordinator.beginRefresh()) {
        await refreshCoordinator.waitForRefresh()
        return api(original)
      }

      /**
       * 2. 我來負責 refresh
       */
      try {
        const tokens = await tokenService(baseURL)

        /**
         * 🔥 更新 token storage（透過 appAuth 直接管理）
         */
        await appAuth.setTokens(tokens)

        /**
         * 放行 queue
         */
        refreshCoordinator.resolveRefresh()

        /**
         * 更新 original request token
         */
        original.headers = original.headers ?? {}
        original.headers.Authorization =
          `Bearer ${tokens.access_token}`

        return api(original)
      } catch (err) {
        /**
         * refresh fail → logout
         */
        refreshCoordinator.rejectRefresh(err as AxiosError)

        const auth = useAuthFacade()
        await refreshCoordinator.runLogout(async () => {
          await auth.logout({ isForce: true })
        })

        throw err
      }
    }
  )
}